import type {
  IAuthService,
  IEventBus,
  IHttpClient,
  Session,
  Unsubscribe,
  UserProfile,
} from "@crialume/sdk";
import type { AuthAdapter, ChangePasswordInput, LoginResult } from "./auth-adapter";
import { isExpired } from "./jwt";
import type { TokenStorage } from "./token-storage";

/** Nova tentativa de renovação após falha de rede. */
const RETRY_MS = 15_000;
// setTimeout estoura acima de ~24,8 dias.
const MAX_TIMEOUT_MS = 2_147_483_647;
/** Renova este tempo antes de expirar (o token do Crialume dura 10 min). */
const DEFAULT_REFRESH_AHEAD_MS = 60_000;
/** Intervalo mínimo entre renovações pedidas por `refreshAccess` (rajada de 403, eventos repetidos). */
const DEFAULT_ACCESS_REFRESH_INTERVAL_MS = 5_000;

/** Executa `task` com exclusão mútua entre abas (padrão: Web Locks API). */
export type SessionLock = <T>(task: () => Promise<T>) => Promise<T>;

export interface AuthServiceOptions {
  adapter: AuthAdapter;
  storage: TokenStorage;
  bus: IEventBus;
  /** Necessário só para login/logout remotos; injetado depois para evitar ciclo http↔auth. */
  getHttp: () => IHttpClient;
  /**
   * Trava da renovação entre abas. Com refresh token rotativo, duas abas
   * renovando ao mesmo tempo apresentariam o mesmo token — o backend trata
   * como reuso e revoga TODAS as sessões.
   */
  lock?: SessionLock;
  refreshAheadMs?: number;
  /** Intervalo mínimo entre chamadas efetivas de `refreshAccess`. */
  accessRefreshIntervalMs?: number;
}

const webLock: SessionLock = (task) =>
  typeof navigator !== "undefined" && navigator.locks
    ? navigator.locks.request("crialume:auth-refresh", task)
    : task();

/**
 * Serviço de sessão genérico. Os métodos são arrow functions para poderem ser
 * passados soltos (ex.: `useSyncExternalStore(auth.subscribe, auth.getSession)`).
 *
 * Com adapter que sabe renovar (`adapter.refresh`), a sessão vive em memória:
 * `initialize()` a recupera pelo cookie no boot, um timer renova antes de
 * expirar e o cliente HTTP chama `refresh()` ao receber 401.
 */
export class AuthService implements IAuthService {
  private session: Session | null = null;
  private token: string | null = null;
  private tenantName: string | undefined;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private refreshing: Promise<boolean> | null = null;
  private lastAccessRefresh = 0;
  private initializing: Promise<void> | null = null;
  private readonly listeners = new Set<(session: Session | null) => void>();
  private readonly stopExternalSync: Unsubscribe | undefined;

  constructor(private readonly options: AuthServiceOptions) {
    this.restore();
    // Logout/login em outra aba reflete aqui sem polling (o CadFrn revalidava a cada 5 min).
    this.stopExternalSync = options.storage.onExternalChange?.(() => this.restore());
  }

  /** Renovação silenciosa disponível (sessão em memória + cookie). */
  get canRefresh(): boolean {
    return typeof this.options.adapter.refresh === "function";
  }

  getSession = (): Session | null => {
    if (this.session && isExpired(this.session.expiresAt)) {
      // Timer atrasado (aba em background). Não muta durante a leitura — pode
      // estar sendo chamado como snapshot no render; a limpeza vai para a fila.
      queueMicrotask(() => {
        if (this.session && isExpired(this.session.expiresAt)) this.onExpired();
      });
      return null;
    }
    return this.session;
  };

  isAuthenticated = (): boolean => this.getSession() !== null;

  getAccessToken = (): string | null => (this.getSession() ? this.token : null);

  /**
   * Recupera a sessão no boot. Sem token salvo e com renovação disponível,
   * tenta o refresh (cookie). Idempotente; nunca rejeita.
   */
  initialize = (): Promise<void> => {
    this.initializing ??= (async () => {
      if (this.session || !this.canRefresh) return;
      await this.refresh().catch(() => false);
    })();
    return this.initializing;
  };

  login = async (credentials: Record<string, unknown>): Promise<Session> => {
    const { adapter, bus, getHttp } = this.options;
    const session = await this.apply(await adapter.login(credentials, getHttp()));
    bus.emit("auth:login", { userId: session.user.id });
    return session;
  };

  /** Troca a senha e adota a sessão nova devolvida pelo backend. */
  changePassword = async (input: ChangePasswordInput): Promise<Session> => {
    const { adapter, getHttp } = this.options;
    if (!adapter.changePassword) throw new Error("Troca de senha não suportada.");
    return this.apply(await adapter.changePassword(input, getHttp()));
  };

  /**
   * Renova a sessão (uma requisição por vez, também entre abas). `true` se
   * há sessão válida ao final. Rejeição do servidor encerra a sessão; falha
   * de rede mantém a atual até ela expirar.
   */
  refresh = (): Promise<boolean> => {
    const { adapter, getHttp } = this.options;
    if (!adapter.refresh) return Promise.resolve(this.isAuthenticated());
    const startedWith = this.token;

    this.refreshing ??= (async () => {
      const lock = this.options.lock ?? webLock;
      try {
        return await lock(async () => {
          // Outra chamada já trocou o token enquanto esperávamos a trava.
          if (this.token && this.token !== startedWith && this.isAuthenticated()) return true;
          const result = await adapter.refresh!(getHttp());
          if (!result) {
            this.onExpired();
            return false;
          }
          await this.apply(result);
          return true;
        });
      } catch {
        // Falha de rede: mantém a sessão atual e tenta de novo enquanto ela valer.
        const valid = this.isAuthenticated();
        if (valid) this.retryLater();
        return valid;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  };

  /**
   * As permissões podem ter mudado no servidor (evento de tempo real, 403 inesperado): renova
   * o token, que traz as atuais. Sem efeito sem sessão ou sem renovação; chamadas seguidas
   * dentro do intervalo mínimo viram uma só.
   */
  refreshAccess = (): void => {
    if (!this.canRefresh || !this.isAuthenticated()) return;
    const now = Date.now();
    const interval = this.options.accessRefreshIntervalMs ?? DEFAULT_ACCESS_REFRESH_INTERVAL_MS;
    if (now - this.lastAccessRefresh < interval) return;
    this.lastAccessRefresh = now;
    void this.refresh();
  };

  logout = (): void => {
    const { adapter, storage, bus, getHttp } = this.options;
    const wasAuthenticated = this.session !== null;
    void Promise.resolve(adapter.logout?.(getHttp())).catch(() => undefined);
    storage.clear();
    this.setSession(null, null);
    if (wasAuthenticated) bus.emit("auth:logout");
  };

  /** Encerra a sessão local por 401 sem possibilidade de renovação. */
  expire = (): void => {
    this.onExpired();
  };

  can = (permission: string): boolean =>
    this.getSession()?.permissions.includes(permission) ?? false;

  canAny = (permissions: readonly string[]): boolean => permissions.some(this.can);

  canAll = (permissions: readonly string[]): boolean => permissions.every(this.can);

  hasRole = (role: string): boolean =>
    this.getSession()?.roles.includes(role.trim().toLowerCase()) ?? false;

  subscribe = (listener: (session: Session | null) => void): Unsubscribe => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  destroy(): void {
    this.stopExternalSync?.();
    this.clearTimer();
    this.listeners.clear();
  }

  /** Valida o token, carrega o perfil (se o adapter souber), persiste e publica. */
  private async apply(result: LoginResult): Promise<Session> {
    const { adapter, storage, getHttp } = this.options;
    const stored = storage.getUser();
    const resolved = adapter.resolveSession(result.accessToken, null);
    if (!resolved || isExpired(resolved.expiresAt)) {
      throw new Error("Token inválido ou expirado.");
    }

    const sameUser = stored?.id === resolved.user.id ? stored : null;
    let user: UserProfile = { ...resolved.user, ...sameUser, ...result.user, id: resolved.user.id };
    let tenantName = result.tenantName ?? (sameUser ? this.tenantName : undefined);
    if (adapter.loadProfile) {
      try {
        const profile = await adapter.loadProfile(getHttp(), result.accessToken);
        user = { ...user, ...profile.user, id: resolved.user.id };
        tenantName = profile.tenantName ?? tenantName;
      } catch {
        // Perfil é só exibição: a sessão segue válida sem ele.
      }
    }

    this.tenantName = tenantName;
    const session = this.withTenantName({ ...resolved, user });
    storage.save(result.accessToken, user);
    this.setSession(result.accessToken, session);
    return session;
  }

  private withTenantName(session: Session): Session {
    return session.tenant && this.tenantName
      ? { ...session, tenant: { ...session.tenant, name: this.tenantName } }
      : session;
  }

  private onExpired(): void {
    const wasAuthenticated = this.session !== null;
    this.options.storage.clear();
    this.setSession(null, null);
    if (wasAuthenticated) this.options.bus.emit("auth:session-expired");
  }

  private restore(): void {
    const { adapter, storage } = this.options;
    const token = storage.getToken();
    const session = token ? adapter.resolveSession(token, storage.getUser()) : null;

    if (!token || !session || isExpired(session.expiresAt)) {
      if (token) storage.clear();
      if (this.session) this.onExpired();
      return;
    }
    if (token !== this.token) this.setSession(token, this.withTenantName(session));
  }

  private setSession(token: string | null, session: Session | null): void {
    this.token = token;
    this.session = session;
    this.clearTimer();
    if (session) {
      const untilExpiry = session.expiresAt - Date.now();
      // Com renovação: troca o token um pouco antes; sem ela, encerra na hora certa.
      const ahead = this.canRefresh
        ? Math.min(this.options.refreshAheadMs ?? DEFAULT_REFRESH_AHEAD_MS, untilExpiry / 2)
        : 0;
      const delay = Math.min(Math.max(0, untilExpiry - ahead), MAX_TIMEOUT_MS);
      this.timer = setTimeout(() => {
        if (this.canRefresh) void this.refresh();
        else this.onExpired();
      }, delay);
    }
    for (const listener of [...this.listeners]) listener(session);
  }

  private retryLater(): void {
    if (!this.session) return;
    this.clearTimer();
    const untilExpiry = this.session.expiresAt - Date.now();
    this.timer = setTimeout(
      () => void this.refresh(),
      Math.max(0, Math.min(RETRY_MS, untilExpiry)),
    );
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
